import { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Plus, Minus } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';

function fmt(ms: number): string {
  const sign = ms < 0 ? '-' : '';
  ms = Math.abs(ms);
  const hh = Math.floor(ms / 3_600_000);
  const mm = Math.floor((ms % 3_600_000) / 60_000);
  const ss = Math.floor((ms % 60_000) / 1000);
  const cs = Math.floor((ms % 1000) / 10);
  if (hh) return `${sign}${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`;
  return `${sign}${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}

export function TimerStopwatch() {
  return (
    <div>
      <ToolHeader
        toolId="timer-stopwatch"
        title="Timer & stopwatch"
        description="Pomodoro-friendly timer plus a millisecond-precise stopwatch."
      />
      <Tabs defaultValue="stopwatch">
        <TabsList className="mb-4">
          <TabsTrigger value="stopwatch">Stopwatch</TabsTrigger>
          <TabsTrigger value="timer">Timer</TabsTrigger>
        </TabsList>
        <TabsContent value="stopwatch">
          <Stopwatch />
        </TabsContent>
        <TabsContent value="timer">
          <Timer />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stopwatch() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [laps, setLaps] = useState<number[]>([]);
  const startRef = useRef<number>(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!running) return;
    startRef.current = Date.now() - elapsed;
    function tick() {
      setElapsed(Date.now() - startRef.current);
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  function lap() {
    setLaps((l) => [elapsed, ...l]);
  }

  return (
    <Card>
      <div className="text-center py-8">
        <div className="font-mono text-6xl md:text-7xl tabular-nums tracking-tight">
          {fmt(elapsed)}
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 mb-6">
        <Button variant="primary" size="lg" onClick={() => setRunning((r) => !r)}>
          {running ? <Pause size={14} /> : <Play size={14} />}
          {running ? 'Pause' : 'Start'}
        </Button>
        <Button variant="secondary" size="lg" onClick={lap} disabled={!running}>
          Lap
        </Button>
        <Button
          variant="ghost"
          size="lg"
          onClick={() => {
            setRunning(false);
            setElapsed(0);
            setLaps([]);
          }}
        >
          <RotateCcw size={14} />
          Reset
        </Button>
      </div>
      {laps.length > 0 && (
        <div className="border-t border-border pt-4 max-h-64 overflow-y-auto">
          {laps.map((t, i) => (
            <div
              key={`${t}-${i}`}
              className="flex items-center justify-between py-1.5 text-sm font-mono"
            >
              <span className="text-fg-subtle">Lap {laps.length - i}</span>
              <span>{fmt(t)}</span>
              <span className="text-fg-subtle">
                {i < laps.length - 1 ? '+' + fmt(t - laps[i + 1]) : fmt(t)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Timer() {
  const [target, setTarget] = useState(25 * 60 * 1000);
  const [remaining, setRemaining] = useState(target);
  const [running, setRunning] = useState(false);
  const targetRef = useRef<number>(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    setRemaining(target);
  }, [target]);

  useEffect(() => {
    if (!running) return;
    targetRef.current = Date.now() + remaining;
    function tick() {
      const r = targetRef.current - Date.now();
      setRemaining(r);
      if (r <= 0) {
        setRunning(false);
        // Beep via Web Audio API
        try {
          const ac = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
          const osc = ac.createOscillator();
          const g = ac.createGain();
          osc.frequency.value = 880;
          osc.connect(g);
          g.connect(ac.destination);
          g.gain.setValueAtTime(0.2, ac.currentTime);
          g.gain.exponentialRampToValueAtTime(0.01, ac.currentTime + 1);
          osc.start();
          osc.stop(ac.currentTime + 1);
        } catch {
          /* ignore */
        }
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const presets = [
    { label: 'Pomodoro', ms: 25 * 60 * 1000 },
    { label: '5m break', ms: 5 * 60 * 1000 },
    { label: '15m', ms: 15 * 60 * 1000 },
    { label: '1h', ms: 60 * 60 * 1000 },
  ];

  return (
    <Card>
      <div className="text-center py-8">
        <div
          className={`font-mono text-6xl md:text-7xl tabular-nums tracking-tight ${
            remaining < 0 ? 'text-danger' : ''
          }`}
        >
          {fmt(Math.max(0, remaining))}
        </div>
      </div>
      <div className="flex items-center justify-center gap-1 mb-6">
        <Button variant="ghost" size="icon" onClick={() => setTarget((t) => Math.max(0, t - 60_000))}>
          <Minus size={14} />
        </Button>
        <Button variant="primary" size="lg" onClick={() => setRunning((r) => !r)}>
          {running ? <Pause size={14} /> : <Play size={14} />}
          {running ? 'Pause' : 'Start'}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTarget((t) => t + 60_000)}
        >
          <Plus size={14} />
        </Button>
        <Button
          variant="ghost"
          size="lg"
          onClick={() => {
            setRunning(false);
            setRemaining(target);
          }}
        >
          <RotateCcw size={14} />
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {presets.map((p) => (
          <button
            key={p.label}
            onClick={() => {
              setTarget(p.ms);
              setRunning(false);
            }}
            className="px-2.5 py-1 text-xs rounded-md border border-border hover:border-accent hover:bg-accent/5 transition"
          >
            {p.label}
          </button>
        ))}
      </div>
    </Card>
  );
}

export default TimerStopwatch;
